import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React from 'react';

import { CREATE, DataAPIContext, DELETE, GET_ONE } from '../api';
import DeleteButton from '../components/DeleteButton';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';

/**
 * Settings -> Email Domain (CP-7402): the operator sends guest emails from its own
 * subdomain (assistant@chat.<operator>.com) instead of assistant@curatedplanet.com.
 *
 * Backend: /v2/edit/email_domain/ (apiv2/views/edit/email_domain.py). Who may see and use
 * this is decided server-side and arrives as `permissions.email_domains` ({read, manage,
 * activate}): Curated Planet staff only today, opened to operators by a backend setting.
 * The list response repeats the same `capabilities`, which is what this page trusts.
 *
 * Flow: add a subdomain -> hand the DNS records to whoever edits the operator's DNS ->
 * Check now until every record is found -> send a test -> Activate.
 */

const RESOURCE = 'email_domain';
const LIVE = ['pending', 'verified', 'active'];

export const STATUS_META = {
    pending: { label: 'Waiting for DNS', color: 'warning' },
    verified: { label: 'Verified', color: 'info' },
    active: { label: 'Active', color: 'success' },
    disabled: { label: 'Deactivated', color: 'default' },
};

const recordKey = (rec) => `${rec.type}|${rec.name}`;

const fieldError = (data, field) => {
    const v = data && data[field];
    if (Array.isArray(v)) return v.join(' ');
    return typeof v === 'string' ? v : null;
};

const firstError = (data, fallback) => fieldError(data, 'domain') || fieldError(data, 'to')
    || fieldError(data, 'test_to') || fieldError(data, 'detail') || (typeof data?.detail === 'string' ? data.detail : null)
    || fallback;

const when = (ts) => (ts ? moment(ts).fromNow() : null);

const CopyButton = ({ value, label }) => {
    const { enqueueSnackbar } = useSnackbar();
    const copy = () => {
        navigator.clipboard.writeText(value).then(
            () => enqueueSnackbar('Copied', { variant: 'success' }),
            () => enqueueSnackbar('Could not copy to the clipboard', { variant: 'error' }),
        );
    };
    return (
        <Tooltip title="Copy">
            <IconButton size="small" onClick={copy} aria-label={`copy ${label}`}>
                <ContentCopyIcon fontSize="inherit" />
            </IconButton>
        </Tooltip>
    );
};

const RecordStatus = ({ rec, checked, verified }) => {
    if (!checked) {
        if (!rec.required) return <Chip size="small" label="Optional" variant="outlined" />;
        return verified ? <Chip size="small" color="success" label="Found" />
            : <Typography variant="caption" color="text.secondary">Not checked yet</Typography>;
    }
    if (checked.found) return <Chip size="small" color="success" label="Found" />;
    if (!rec.required) return <Chip size="small" label="Optional" variant="outlined" />;
    return <Chip size="small" color="warning" label="Not found" />;
};

const mono = { fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' };

// Below tablet width a four-column table squeezes the long DKIM names into one-character
// columns, so each record becomes its own block (jsdom has no matchMedia: tests get the table).
const RecordCards = ({ row, byKey, verified }) => (
    <Box data-testid="email-domain-records">
        {(row.records || []).map((rec) => (
            <Box
                key={recordKey(rec)}
                data-testid={`record-${rec.type}-${rec.host}`}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1 }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{rec.type}</Typography>
                    <RecordStatus rec={rec} checked={byKey[recordKey(rec)]} verified={verified} />
                </Box>
                <Typography variant="caption" color="text.secondary">Name (host)</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={mono}>{rec.host}</Typography>
                    <CopyButton value={rec.host} label={`${rec.type} name ${rec.host}`} />
                </Box>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ overflowWrap: 'anywhere', mb: 1 }}>
                    {rec.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">Value</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography sx={mono}>{rec.value}</Typography>
                    <CopyButton value={rec.value} label={`${rec.type} value for ${rec.host}`} />
                </Box>
            </Box>
        ))}
    </Box>
);

const RecordsTable = ({ row, checks, verified }) => {
    const byKey = Object.fromEntries((checks || []).map((c) => [recordKey(c), c]));
    const narrow = useMediaQuery((theme) => theme.breakpoints.down('md'));
    if (narrow) return <RecordCards row={row} byKey={byKey} verified={verified} />;
    return (
        <Table size="small" data-testid="email-domain-records">
            <TableHead>
                <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Name (host)</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell>Status</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {(row.records || []).map((rec) => (
                    <TableRow key={recordKey(rec)} data-testid={`record-${rec.type}-${rec.host}`}>
                        <TableCell>{rec.type}</TableCell>
                        <TableCell sx={{ maxWidth: 280 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' }}>
                                    {rec.host}
                                </Typography>
                                <CopyButton value={rec.host} label={`${rec.type} name ${rec.host}`} />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                {rec.name}
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography sx={{ fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' }}>
                                    {rec.value}
                                </Typography>
                                <CopyButton value={rec.value} label={`${rec.type} value for ${rec.host}`} />
                            </Box>
                        </TableCell>
                        <TableCell><RecordStatus rec={rec} checked={byKey[recordKey(rec)]} verified={verified} /></TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

// `destructive`: the OK is the shared DELETE (outlined red + trash, CP-5545).
const Confirm = ({ open, title, children, okText, onOk, onClose, busy, destructive = false }) => (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent><DialogContentText component="div">{children}</DialogContentText></DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            {destructive ? (
                <DeleteButton onClick={onOk} disabled={busy}>{okText}</DeleteButton>
            ) : (
                <Button onClick={onOk} variant="contained" disabled={busy}>{okText}</Button>
            )}
        </DialogActions>
    </Dialog>
);

const AddDomainForm = ({ suggested, operatorName, onCreated }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [domain, setDomain] = React.useState(suggested || '');
    const [displayName, setDisplayName] = React.useState('');
    const [error, setError] = React.useState(null);
    const [inUse, setInUse] = React.useState(null);
    const [busy, setBusy] = React.useState(false);
    // A successful add swaps this form for the domain card, unmounting it mid-request.
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);

    const submit = async (force = false) => {
        setBusy(true);
        setError(null);
        try {
            const res = await dataAPI(CREATE, RESOURCE, {
                data: { domain: domain.trim(), display_name: displayName.trim(), ...(force ? { force: true } : {}) },
            });
            if (res?.statusCode === 201) {
                if (res.data?.ok === false) {
                    enqueueSnackbar(`Domain added, but Amazon SES did not create it yet: ${res.data.detail}. Use Check now to retry.`,
                        { variant: 'warning', autoHideDuration: 12000 });
                } else {
                    enqueueSnackbar('Domain added. Send the DNS records to whoever manages DNS.', { variant: 'success' });
                }
                onCreated(res.data.domain);
            } else if (res?.data?.code === 'name_in_use') {
                setInUse(res.data);
            } else {
                setError(firstError(res?.data, 'Could not add the domain.'));
            }
        } catch (e) {
            if (mounted.current) setError('Could not add the domain.');
        } finally {
            if (mounted.current) setBusy(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }} data-testid="email-domain-add">
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>Add a sending domain</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use a subdomain nobody uses yet; you don&apos;t need to create it first. We suggest <b>chat.</b> plus
                the operator&apos;s website domain. Once you save, we&apos;ll show the DNS records to add; publish them
                within 72 hours. Avoid <b>mail.</b>: it is often already the webmail or web server, and our records
                can&apos;t share a name with it.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                    label="Subdomain"
                    size="small"
                    value={domain}
                    onChange={(e) => { setDomain(e.target.value); setInUse(null); setError(null); }}
                    placeholder="chat.example.com"
                    error={!!error}
                    helperText={error || `For example ${suggested || 'chat.yourtours.com'}`}
                    sx={{ minWidth: 300 }}
                    inputProps={{ 'aria-label': 'Subdomain' }}
                />
                <TextField
                    label="Sender name (optional)"
                    size="small"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={operatorName || ''}
                    helperText={`Guests see this name. Defaults to ${operatorName || 'the operator name'}.`}
                    sx={{ minWidth: 260 }}
                />
                <Button variant="contained" onClick={() => submit(false)} disabled={busy || !domain.trim()} sx={{ mt: 0.25 }}>
                    {busy ? <CircularProgress size={20} /> : 'Add domain'}
                </Button>
            </Box>
            {inUse && (
                <Alert
                    severity="warning"
                    sx={{ mt: 1 }}
                    action={<Button color="inherit" size="small" onClick={() => submit(true)} disabled={busy}>Use it anyway</Button>}
                >
                    {inUse.domain}
                </Alert>
            )}
        </Paper>
    );
};

const DomainCard = ({ row, caps, enabled, defaultTestTo, onRow, onRemoved }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { enqueueSnackbar } = useSnackbar();
    const [checks, setChecks] = React.useState(null);
    const [problems, setProblems] = React.useState([]);
    const [busy, setBusy] = React.useState(null);
    const [testTo, setTestTo] = React.useState(defaultTestTo || '');
    const [emailTo, setEmailTo] = React.useState('');
    const [dialog, setDialog] = React.useState(null);
    // Remove drops this card from the page while the request is still settling.
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);

    const meta = STATUS_META[row.status] || { label: row.status, color: 'default' };
    const verified = !!(row.dkim_verified_ts && row.mx_verified_ts);
    const canActivate = caps?.activate && (row.status === 'verified' || (row.status === 'disabled' && verified));

    const post = async (name, suffix, data, okMessage) => {
        setBusy(name);
        try {
            const res = await dataAPI(CREATE, RESOURCE, { urlSuffix: `${row.id}/${suffix}/`, data: data || {} });
            if (res?.data?.domain) onRow(res.data.domain);
            if (res?.statusCode >= 400 && !res?.data?.domain) {
                enqueueSnackbar(firstError(res?.data, 'Request failed.'), { variant: 'error' });
                return null;
            }
            if (res?.data?.ok === false) {
                if (name !== 'check') enqueueSnackbar(firstError(res.data, 'Request failed.'), { variant: 'error', autoHideDuration: 12000 });
            } else if (okMessage) {
                enqueueSnackbar(okMessage, { variant: 'success' });
            }
            return res?.data;
        } catch (e) {
            enqueueSnackbar('Request failed.', { variant: 'error' });
            return null;
        } finally {
            setBusy(null);
        }
    };

    const check = async () => {
        const data = await post('check', 'check');
        if (data?.detail) {
            setChecks(data.detail.records || []);
            setProblems(data.detail.problems || []);
            if (data.ok) enqueueSnackbar('Every record is in place.', { variant: 'success' });
        }
    };

    const remove = async () => {
        setBusy('remove');
        try {
            const res = await dataAPI(DELETE, RESOURCE, { id: row.id });
            if (res?.statusCode === 204) {
                enqueueSnackbar(`${row.domain} removed`, { variant: 'success' });
                onRemoved(row.id);
            } else {
                enqueueSnackbar(firstError(res?.data, 'Could not remove the domain.'), { variant: 'error' });
            }
        } finally {
            if (mounted.current) {
                setBusy(null);
                setDialog(null);
            }
        }
    };

    const copyInstructions = () => {
        navigator.clipboard.writeText(row.instructions || '').then(
            () => enqueueSnackbar('Instructions copied', { variant: 'success' }),
            () => enqueueSnackbar('Could not copy to the clipboard', { variant: 'error' }),
        );
    };

    return (
        <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }} data-testid={`email-domain-${row.id}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontFamily: 'monospace', fontSize: 18 }}>{row.domain}</Typography>
                <Chip label={meta.label} color={meta.color} size="small" data-testid="email-domain-status" />
            </Box>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
                Guests will see: <b>{row.from_header}</b>
            </Typography>
            {row.last_checked_ts && (
                <Typography variant="caption" color="text.secondary">
                    Last checked {when(row.last_checked_ts)}
                    {row.status === 'active' && row.sending_enabled_ts ? ` · active since ${moment(row.sending_enabled_ts).format('LL')}` : ''}
                </Typography>
            )}

            {row.status !== 'active' && row.status !== 'disabled' && (
                <>
                    <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>1. Publish these DNS records</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        The operator (or whoever manages their DNS) adds these to <b>{row.zone}</b>. Most DNS panels
                        want the short host on the first line; the full name is underneath it.
                    </Typography>
                    {(row.records || []).length > 0 ? <RecordsTable row={row} checks={checks} verified={verified} /> : (
                        <Alert severity="warning">No records yet: Amazon SES has not created the domain. Use Check now to retry.</Alert>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" onClick={copyInstructions} disabled={!row.instructions}>Copy instructions</Button>
                        {caps?.manage && (
                            <Button size="small" variant="outlined" onClick={() => setDialog('email')} disabled={!row.instructions}>
                                Email instructions…
                            </Button>
                        )}
                    </Box>

                    <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>2. Verify</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Records usually show up within minutes of being added, sometimes a few hours.
                        We also re-check every hour on our own.
                    </Typography>
                    {caps?.manage && (
                        <Button variant="contained" size="small" onClick={check} disabled={!!busy}>
                            {busy === 'check' ? <CircularProgress size={18} /> : 'Check now'}
                        </Button>
                    )}
                    {problems.length > 0 && (
                        <Alert severity="info" sx={{ mt: 1.5 }} data-testid="email-domain-problems">
                            From the last check:
                            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                {problems.map((p) => <li key={p}>{p}</li>)}
                            </ul>
                        </Alert>
                    )}
                    {!problems.length && row.last_check_error && row.status === 'pending' && (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                            Last result: {row.last_check_error}
                        </Typography>
                    )}
                </>
            )}

            {(row.status === 'verified' || row.status === 'disabled') && (
                <>
                    <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>
                        {row.status === 'disabled' ? 'Turn it back on' : '3. Test and activate'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Activating sends one test email to the address below, then every email this operator sends
                        comes from {row.address}. Guest replies come back to the same domain and land in chat as before.
                    </Typography>
                    {!enabled && (
                        <Alert severity="info" sx={{ mb: 1.5 }}>
                            Operator sending domains are switched off platform-wide, so activating has no effect on
                            outgoing mail until that switch is on.
                        </Alert>
                    )}
                    {caps?.manage && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <TextField
                                label="Test address"
                                size="small"
                                value={testTo}
                                onChange={(e) => setTestTo(e.target.value)}
                                sx={{ minWidth: 280 }}
                                inputProps={{ 'aria-label': 'Test address' }}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => post('test', 'send_test', { to: testTo.trim() }, `Test email sent to ${testTo.trim()}`)}
                                disabled={!!busy || !testTo.trim() || !verified}
                            >
                                {busy === 'test' ? <CircularProgress size={18} /> : 'Send test email'}
                            </Button>
                            {canActivate && (
                                <Button variant="contained" onClick={() => setDialog('activate')} disabled={!!busy || !testTo.trim()}>
                                    Activate
                                </Button>
                            )}
                        </Box>
                    )}
                    {caps?.manage && !caps?.activate && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Curated Planet activates the domain once you are ready. Contact support.
                        </Typography>
                    )}
                </>
            )}

            {row.status === 'active' && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        Every email this operator sends now comes from <b>{row.address}</b>.
                    </Typography>
                    {!enabled && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            Operator sending domains are switched off platform-wide, so mail still goes out from the shared address.
                        </Alert>
                    )}
                    {caps?.activate && (
                        <Button sx={{ mt: 1.5 }} color="error" variant="outlined" size="small" onClick={() => setDialog('deactivate')} disabled={!!busy}>
                            Deactivate
                        </Button>
                    )}
                </Box>
            )}

            {caps?.manage && (row.status === 'pending' || row.status === 'disabled') && (
                <Box sx={{ mt: 2.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                    <DeleteButton size="small" onClick={() => setDialog('remove')} disabled={!!busy}>Delete domain</DeleteButton>
                </Box>
            )}

            <Confirm
                open={dialog === 'activate'}
                title={`Activate ${row.domain}?`}
                okText="Send test and activate"
                busy={busy === 'activate'}
                onClose={() => setDialog(null)}
                onOk={async () => {
                    const data = await post('activate', 'activate', { test_to: testTo.trim() }, `${row.domain} is active`);
                    if (data) setDialog(null);
                }}
            >
                A test email goes to <b>{testTo.trim()}</b>. If it sends, every email this operator sends switches
                to <b>{row.from_header}</b> right away. You can deactivate at any time.
            </Confirm>
            <Confirm
                open={dialog === 'deactivate'}
                title={`Deactivate ${row.domain}?`}
                okText="Deactivate"
                busy={busy === 'deactivate'}
                onClose={() => setDialog(null)}
                onOk={async () => {
                    const data = await post('deactivate', 'deactivate', {}, `${row.domain} deactivated`);
                    if (data) setDialog(null);
                }}
            >
                Emails go back to the operator&apos;s previous sender with the next message. Replies to the old address keep reaching chat.
            </Confirm>
            <Confirm
                open={dialog === 'remove'}
                title={`Delete ${row.domain}?`}
                okText="Delete"
                destructive
                busy={busy === 'remove'}
                onClose={() => setDialog(null)}
                onOk={remove}
            >
                The operator can delete the DNS records afterwards. To use the domain again later, add it again.
            </Confirm>
            <Dialog open={dialog === 'email'} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Email the DNS instructions</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Sends the records and step-by-step notes to whoever edits the operator&apos;s DNS (their web agency, IT person, or the operator).
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Send to"
                        value={emailTo}
                        onChange={(e) => setEmailTo(e.target.value)}
                        inputProps={{ 'aria-label': 'Send to' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(null)} disabled={busy === 'email'}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={busy === 'email' || !emailTo.trim()}
                        onClick={async () => {
                            const data = await post('email', 'send_instructions', { to: emailTo.trim() }, `Instructions sent to ${emailTo.trim()}`);
                            if (data) setDialog(null);
                        }}
                    >
                        Send
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

const EmailDomainsPage = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const { user, tourOperator } = React.useContext(ContextStore);
    const [state, setState] = React.useState({ loading: true, error: null, data: null });

    const load = React.useCallback(async () => {
        try {
            const res = await dataAPI(GET_ONE, RESOURCE, {});
            if (res?.statusCode === 200) {
                setState({ loading: false, error: null, data: res.data });
            } else if (res?.statusCode === 403) {
                setState({ loading: false, error: 'This page is not available for your account.', data: null });
            } else {
                setState({ loading: false, error: 'Could not load email domains.', data: null });
            }
        } catch (e) {
            setState({ loading: false, error: 'Could not load email domains.', data: null });
        }
    }, [dataAPI]);

    React.useEffect(() => { load(); }, [load]);

    const data = state.data;
    const caps = data?.capabilities || user?.permissions?.email_domains || null;
    const rows = data?.results || [];
    const live = rows.find((r) => LIVE.includes(r.status));
    const previous = rows.filter((r) => r !== live);
    const shown = live || previous[0] || null;
    const older = previous.filter((r) => r !== shown);

    const putRow = (row) => setState((prev) => {
        const results = prev.data?.results || [];
        const exists = results.some((r) => r.id === row.id);
        return {
            ...prev,
            data: { ...prev.data, results: exists ? results.map((r) => (r.id === row.id ? row : r)) : [row, ...results] },
        };
    });
    const dropRow = (id) => setState((prev) => ({
        ...prev,
        data: { ...prev.data, results: (prev.data?.results || []).filter((r) => r.id !== id) },
    }));

    return (
        <Box sx={{ p: 3, maxWidth: 1000 }}>
            <PageMeta title="Email Domain" />
            <PageHeading sx={{ mb: 1 }}>Email Domain</PageHeading>
            <Typography variant="body2" color="text.secondary">
                Send guest emails from the operator&apos;s own address, such as assistant@chat.example.com, instead of
                {' '}assistant@curatedplanet.com. Guests recognise the sender, and replies still arrive in chat.
            </Typography>

            {state.loading && <Box sx={{ mt: 3 }}><CircularProgress size={28} /></Box>}
            {state.error && <Alert severity="error" sx={{ mt: 2 }}>{state.error}</Alert>}

            {data && (
                <>
                    {!data.inbound_live && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Inbound email is not live in this environment, so replies to an operator domain would not reach chat.
                            Activation is blocked until it is.
                        </Alert>
                    )}
                    {shown && (
                        <DomainCard
                            key={shown.id}
                            row={shown}
                            caps={caps}
                            enabled={!!data.enabled}
                            defaultTestTo={user?.email || ''}
                            onRow={putRow}
                            onRemoved={dropRow}
                        />
                    )}
                    {!live && caps?.manage && (
                        <AddDomainForm
                            suggested={data.suggested_domain}
                            operatorName={tourOperator?.name}
                            onCreated={putRow}
                        />
                    )}
                    {!shown && !caps?.manage && (
                        <Typography variant="body2" sx={{ mt: 2 }}>No sending domain is set up for this operator.</Typography>
                    )}
                    {older.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Previous domains</Typography>
                            {older.map((r) => (
                                <Typography key={r.id} variant="body2" color="text.secondary">
                                    {r.domain} · {(STATUS_META[r.status] || {}).label || r.status}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default EmailDomainsPage;
